import React, { useState } from 'react';
import DatePicker from 'react-datepicker';

import { addToCollectionAutoGenerateId } from '../services/database';
import {
    CheckboxField,
    CloseButton,
    ErrorMessage,
    FormActions,
    FormButton,
    FormField,
    FormGrid,
    FormInput,
    FormSection,
    FormSectionDescription,
    FormSectionTitle,
    FormSelect,
    FormTextarea,
    ModalBackdrop,
    ModalBody,
    ModalCard,
    ModalForm,
    ModalHeader,
    ModalSubtitle,
    ModalTitle,
} from '../screens/Styles/PackageSubscriptionForm';

const packageOptions = [
    'Pretplatnički paket: BASIC',
    'Pretplatnički paket: BETTER VALUE',
    'Pretplatnički paket: PREMIUM',
    'Pretplatnički paket: PREMIUM | PUBLIC',
    'Pretplatnički paket: REVICON CLUB',
];
const yearOptions = ['2024', '2025', '2026', '2027'];

const initialInputs = () => ({
    user: '',
    company: '',
    customer_type: '',
    package_name: '',
    description: '',
    email: '',
    address: '',
    city: '',
    phone: '',
    zip: '',
    invoiceId: '',
});

export default function AddPackageSubscription({ showModal, handleShowModal }) {
    const [inputs, setInputs] = useState(initialInputs);
    const [year, setYear] = useState('2027');
    const [startDate, setStartDate] = useState(new Date());
    const [isPaid, setIsPaid] = useState(false);
    const [paidDate, setPaidDate] = useState(new Date());
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const setField = (field, value) => {
        setInputs(current => ({ ...current, [field]: value }));
    };

    const setCustomerType = value => {
        setInputs(current => ({
            ...current,
            customer_type: value,
            company: value === 'person' ? '' : current.company,
        }));
    };

    const resetForm = () => {
        setInputs(initialInputs());
        setYear('2027');
        setStartDate(new Date());
        setIsPaid(false);
        setPaidDate(new Date());
        setError('');
    };

    const closeModal = () => {
        if (saving) return;
        handleShowModal();
    };

    const handleSubmit = async event => {
        event.preventDefault();
        setError('');

        if (!inputs.package_name) {
            setError('Odaberite paket pretplate.');
            return;
        }
        if (!['company', 'person'].includes(inputs.customer_type)) {
            setError('Odaberite da li je kupac kompanija ili fizičko lice.');
            return;
        }
        if (!year) {
            setError('Odaberite godinu pretplate.');
            return;
        }

        setSaving(true);

        try {
            await addToCollectionAutoGenerateId('package_subscriptions', {
                user: inputs.user,
                date: startDate.toString(),
                package_name: inputs.package_name,
                city: inputs.city,
                address: inputs.address,
                phone: inputs.phone,
                company: inputs.customer_type === 'person' ? '' : inputs.company,
                fizicko_lice: inputs.customer_type === 'person',
                email: inputs.email,
                zip: inputs.zip,
                description: inputs.description,
                year,
                paid: isPaid,
                paid_date: isPaid ? paidDate.toString() : '',
                invoiceId: inputs.invoiceId,
            });
            resetForm();
            setSaving(false);
            handleShowModal();
        } catch (saveError) {
            console.error(saveError);
            setSaving(false);
            setError('Pretplata nije sačuvana. Provjerite podatke i pokušajte ponovo.');
        }
    };

    return (
        <ModalBackdrop $show={showModal} role="presentation">
            <ModalCard role="dialog" aria-modal="true" aria-labelledby="add-subscription-title">
                <ModalHeader>
                    <div>
                        <ModalTitle id="add-subscription-title">Dodaj pretplatu</ModalTitle>
                        <ModalSubtitle>Ručni administratorski unos godišnje pretplate.</ModalSubtitle>
                    </div>
                    <CloseButton
                        type="button"
                        onClick={closeModal}
                        title="Zatvori"
                        aria-label="Zatvori"
                    >
                        <i className="fas fa-times" />
                    </CloseButton>
                </ModalHeader>

                <ModalBody>
                    <ModalForm onSubmit={handleSubmit}>
                        <FormSection>
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                            <FormSectionTitle>Pretplata i kupac</FormSectionTitle>
                            <FormSectionDescription>
                                Obavezno odaberite paket, godinu i tip kupca.
                            </FormSectionDescription>
                            <FormGrid>
                                <FormField>
                                    Paket
                                    <FormSelect
                                        value={inputs.package_name}
                                        onChange={event => setField('package_name', event.target.value)}
                                    >
                                        <option value="">Izaberite paket</option>
                                        {packageOptions.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </FormSelect>
                                </FormField>
                                <FormField>
                                    Godina
                                    <FormSelect value={year} onChange={event => setYear(event.target.value)}>
                                        {yearOptions.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </FormSelect>
                                </FormField>
                                <FormField>
                                    Ime i prezime
                                    <FormInput
                                        value={inputs.user}
                                        onChange={event => setField('user', event.target.value)}
                                        placeholder="Ime i prezime"
                                    />
                                </FormField>
                                <FormField>
                                    Tip kupca
                                    <FormSelect
                                        value={inputs.customer_type}
                                        onChange={event => setCustomerType(event.target.value)}
                                    >
                                        <option value="">Odaberite tip kupca</option>
                                        <option value="company">Kompanija</option>
                                        <option value="person">Fizičko lice</option>
                                    </FormSelect>
                                </FormField>
                                {inputs.customer_type !== 'person' && (
                                    <FormField $full>
                                        Kompanija
                                        <FormInput
                                            value={inputs.company}
                                            onChange={event => setField('company', event.target.value)}
                                            placeholder="Naziv kompanije"
                                        />
                                    </FormField>
                                )}
                            </FormGrid>
                        </FormSection>

                        <FormSection>
                            <FormSectionTitle>Kontakt</FormSectionTitle>
                            <FormSectionDescription>Kontakt i adresa kupca.</FormSectionDescription>
                            <FormGrid>
                                <FormField>
                                    Email
                                    <FormInput
                                        type="email"
                                        value={inputs.email}
                                        onChange={event => setField('email', event.target.value)}
                                        placeholder="email@primjer.ba"
                                    />
                                </FormField>
                                <FormField>
                                    Telefon
                                    <FormInput
                                        type="tel"
                                        value={inputs.phone}
                                        onChange={event => setField('phone', event.target.value)}
                                        placeholder="Broj telefona"
                                    />
                                </FormField>
                                <FormField $full>
                                    Adresa
                                    <FormInput
                                        value={inputs.address}
                                        onChange={event => setField('address', event.target.value)}
                                        placeholder="Ulica i broj"
                                    />
                                </FormField>
                                <FormField>
                                    Grad
                                    <FormInput
                                        value={inputs.city}
                                        onChange={event => setField('city', event.target.value)}
                                        placeholder="Grad"
                                    />
                                </FormField>
                                <FormField>
                                    Poštanski broj
                                    <FormInput
                                        value={inputs.zip}
                                        onChange={event => setField('zip', event.target.value)}
                                        placeholder="Poštanski broj"
                                    />
                                </FormField>
                            </FormGrid>
                        </FormSection>

                        <FormSection>
                            <FormSectionTitle>Detalji i naplata</FormSectionTitle>
                            <FormSectionDescription>Datum prijave, status naplate i interna bilješka.</FormSectionDescription>
                            <FormGrid>
                                <FormField>
                                    Datum prijave
                                    <DatePicker
                                        selected={startDate}
                                        onChange={setStartDate}
                                        dateFormat="dd.MM.yyyy"
                                    />
                                </FormField>
                                <FormField>
                                    Predračun
                                    <FormInput
                                        value={inputs.invoiceId}
                                        onChange={event => setField('invoiceId', event.target.value)}
                                        placeholder="Broj predračuna"
                                    />
                                </FormField>
                                <CheckboxField>
                                    <input
                                        type="checkbox"
                                        checked={isPaid}
                                        onChange={event => setIsPaid(event.currentTarget.checked)}
                                    />
                                    Pretplata je plaćena
                                </CheckboxField>
                                {isPaid && (
                                    <FormField>
                                        Datum plaćanja
                                        <DatePicker
                                            selected={paidDate}
                                            onChange={setPaidDate}
                                            dateFormat="dd.MM.yyyy"
                                        />
                                    </FormField>
                                )}
                                <FormField $full>
                                    Opis
                                    <FormTextarea
                                        value={inputs.description}
                                        onChange={event => setField('description', event.target.value)}
                                        placeholder="Interna bilješka o pretplati"
                                    />
                                </FormField>
                            </FormGrid>
                        </FormSection>

                        <FormActions>
                            <FormButton type="button" onClick={closeModal}>Odustani</FormButton>
                            <FormButton type="submit" $primary disabled={saving}>
                                <i className="fas fa-plus" /> {saving ? 'Čuvanje…' : 'Dodaj pretplatu'}
                            </FormButton>
                        </FormActions>
                    </ModalForm>
                </ModalBody>
            </ModalCard>
        </ModalBackdrop>
    );
}
